import React, {useEffect, useMemo, useState} from 'react';
import './UserInfo.scss';
import Q_InputFeedback from '../../../components/Q_Elements/Q_InputFeedback/Q_InputFeedback';
import TableUserInfo from './TableUserInfo';
import RelationshipUserInfo from './RelationshipUserInfo';
import {getUserByEmailQuery, getUserByIdQuery} from "../../../graphql/queries";
import {useLazyQuery} from "react-apollo";
import gql from "graphql-tag";
import iconReplay from '../../Playtime/icon_replay.png';
import CourseProgressOverview from './CourseProgressOverview';
import SubscriptionDetailsTable from './SubscriptionDetailsTable';

function UserInfo(props) {
  const loggedInUser = (props && props.user) || null;
  const [emailVal, setEmailVal] = useState('');
  const [lastQuery, setLastQuery] = useState('');
  const [lastType, setLastType] = useState('email'); // 'email' | 'id'
  const [adminEmails, setAdminEmails] = useState([]);

  const [fetchUser, {loading, error, data = {}, refetch}] = useLazyQuery(
    gql(getUserByEmailQuery),
    {
      fetchPolicy: 'no-cache',
      notifyOnNetworkStatusChange: true,
    }
  );
  const [fetchUserById, {loading: loadingById, error: errorById, data: dataById = {}, refetch: refetchById}] = useLazyQuery(
    gql(getUserByIdQuery),
    {
      fetchPolicy: 'no-cache',
      notifyOnNetworkStatusChange: true,
    }
  );

  // Only allow access on localhost/127.0.0.1 (dev/admin use)
  const isLocalhost = typeof window !== 'undefined' && ['localhost', '127.0.0.1', '::1'].includes(window.location.hostname);

  // Add a noindex,nofollow meta for this page so crawlers won't index if exposed
  useEffect(() => {
    const meta = document.createElement('meta');
    meta.name = 'robots';
    meta.content = 'noindex,nofollow';
    document.head.appendChild(meta);
    // Autofill from query params (?email=... or ?id=...) and trigger load
    try {
      const params = new URLSearchParams(window.location.search || '');
      const qpEmail = params.get('email');
      const qpId = params.get('id');
      if (qpEmail) {
        setEmailVal(qpEmail);
        setLastQuery(qpEmail);
        setLastType('email');
        fetchUser({ variables: { email: qpEmail } });
      } else if (qpId) {
        setEmailVal(qpId);
        setLastQuery(qpId);
        setLastType('id');
        fetchUserById({ variables: { id: qpId } });
      }
    } catch (_) {}
    // Load public-config to get admin emails
    (async () => {
      try {
        const base = (typeof process !== 'undefined' && process.env && process.env.REACT_APP_BACKEND_URL) || '';
        const res = await fetch(`${base}/public-config`, { credentials: 'include' });
        const j = await res.json();
        if (j && j.ok && Array.isArray(j.adminEmails)) {
          setAdminEmails(j.adminEmails.map((e) => String(e).toLowerCase()));
        }
      } catch (_) {}
    })();
    return () => {
      try { document.head.removeChild(meta); } catch (_) {}
    };
  }, []);

  const onSubmit = () => {
    const identifier = emailVal.trim();
    if (!identifier) return;
    const type = identifier.includes('@') ? 'email' : 'id';
    setLastQuery(identifier);
    setLastType(type);
    if (type === 'email') {
      fetchUser({ variables: { email: identifier } });
    } else {
      fetchUserById({ variables: { id: identifier } });
    }
  }

  const onRefresh = async () => {
    try {
      const identifier = (lastQuery || emailVal).trim();
      if (!identifier) return;
      if (lastType === 'email') {
        if (typeof refetch === 'function') {
          await refetch({ email: identifier });
        } else {
          fetchUser({ variables: { email: identifier } });
        }
      } else {
        if (typeof refetchById === 'function') {
          await refetchById({ id: identifier });
        } else {
          fetchUserById({ variables: { id: identifier } });
        }
      }
    } catch (_) {}
  };

  // Normalize data so downstream components can keep reading data.userByEmail
  const normalizedData = useMemo(() => {
    if (lastType === 'email') {
      const u = data?.userByEmail ?? null;
      return u ? { user: u } : {};
    }
    const u = (dataById?.userById || dataById?.getUserByIdQuery) ?? null;
    return u ? { user: u } : {};
  }, [lastType, data, dataById]);

  // Log the full user object whenever it changes
  useEffect(() => {
    if (normalizedData?.user) {
      // eslint-disable-next-line no-console
      console.log('[Admin UserInfo] User', normalizedData.user);
    }
  }, [normalizedData?.user]);

  const isAdmin = !!(loggedInUser && loggedInUser.email && adminEmails.includes(String(loggedInUser.email).toLowerCase()));

  return (
    <>
      {(!isLocalhost) ? null :
      <>
      {isAdmin ? (
        <div className="userinfo-container">
          <div className="userinfo-top">
            <div className="userinfo-header">
              <div className="userinfo-header__title">User Info</div>
              <div className="userinfo-refresh" onClick={onRefresh} title="Refresh">
                <img src={iconReplay} alt="refresh"/>
              </div>
              <a className="userinfo_button noselect" href="/admin/dashboard" style={{ marginLeft: 8, textDecoration: 'none', display: 'inline-flex', alignItems: 'center', height: 40 }}>Dashboard</a>
            </div>
            <div className="userinfo-container__email">
              <div className="userinfo-container__field">
                <Q_InputFeedback label={'Email or ID'} onInputChange={setEmailVal} initialValue={emailVal}
                                 isTextArea={false} onEnterKey={onSubmit}/>
              </div>
              <div className="userinfo_button noselect"
                   onClick={onSubmit}>
                ↳
              </div>
            </div>
            <div className="userinfo-container__tableUserInfo">
              <TableUserInfo data={normalizedData} adminEmails={adminEmails}/>
            </div>
          </div>
          <div className="userinfo-container__RelationshipUserInfo">
            <div className="admin-section admin-section--subscription">
              <h3 className="relationship-user-info__title">Subscription</h3>
              <SubscriptionDetailsTable subscription={normalizedData.user?.subscription} />
            </div>
            <div className="admin-section">
              <h3 className="relationship-user-info__title">Course</h3>
              {normalizedData.user?.userProgress ? (
                <CourseProgressOverview userProgress={normalizedData.user.userProgress} userId={normalizedData.user?.id} />
              ) : (
                <div style={{ padding: '0 18px 8px 18px' }}><div className="no-data">No data</div></div>
              )}
            </div>
            <div className="admin-section">
              <h3 className="relationship-user-info__title">Felt</h3>
              {Array.isArray(normalizedData.user?.relationships) && normalizedData.user.relationships.length > 0 ? (
                <RelationshipUserInfo relationshipsData={normalizedData.user?.relationships}/>
              ) : (
                <div className="no-data">No data</div>
              )}
            </div>
          </div>
        </div>
      ) : (
        <div style={{ padding: 16 }}>
          <div className="no-data">Go back to homepage</div>
          <a className="userinfo_button noselect" href="/" style={{ display: 'inline-flex', marginTop: 8 }}>Home</a>
        </div>
      )}
      </>}
    </>
  );
}

export default UserInfo;
